import { getRequestWithAuth, postRequestWithAuth } from '../api';

// Types
export type ReportStatus = 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED';

// Presentation types
export type FindingSeverity = 'positive' | 'warning' | 'critical';
export type Effort = 'Quick win' | 'Medium effort' | 'Major project';

export interface Finding {
  severity: FindingSeverity;
  headline: string;
  subline: string;
  what_happening: string;
  why_matters: string;
  what_to_do: string;
}

export interface Action {
  title: string;
  effort: Effort;
  impact: string;
  steps: string[];
  tools?: string[];
}

export interface RecipePresentation {
  finding: Finding;
  action: Action | null;
}

export interface RecipeMetrics {
  recipe_name: string;
  success: boolean;
  metrics: Record<string, any>;
  error?: string;
}

export interface ReportSummary {
  id: string;
  store_name: string;
  status: ReportStatus;
  leak_score: number | null;
  grade: string | null;
  total_recoverable: number | null;
  currency: string;
  created_at: string;
  completed_at: string | null;
}

export interface ReportDetail {
  id: string;
  connection_id: string;
  store_name: string;
  store_url: string;
  period_start: string;
  period_end: string;
  period_days: number;
  status: ReportStatus;
  error_message: string | null;
  leak_score: number | null;
  grade: string | null;
  total_recoverable: number | null;
  currency: string;
  recipe_results: RecipeMetrics[];
  presentations: Record<string, RecipePresentation>;
  created_at: string;
  completed_at: string | null;
  generation_time_seconds: number | null;
}

export interface GenerateReportResponse {
  report_id: string;
  status: string;
  message: string;
}

export interface ReportStatusResponse {
  report_id: string;
  status: ReportStatus;
  error_message: string | null;
}

// API functions
export const generateReport = async (
  connectionId: string,
  periodDays: number = 90
): Promise<GenerateReportResponse> => {
  const response = await postRequestWithAuth(
    '/api/v1/ecommerce/reports/generate',
    {
      connection_id: connectionId,
      period_days: periodDays,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );
  return response;
};

export const generatePartnerReport = async (
  connectionId: string,
  periodDays: number = 90
): Promise<GenerateReportResponse> => {
  const response = await postRequestWithAuth(
    '/api/v1/ecommerce/reports/generatePartnerReport',
    {
      connection_id: connectionId,
      period_days: periodDays,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );
  return response;
};

export const getReport = async (reportId: string): Promise<ReportDetail> => {
  const response = await getRequestWithAuth(
    `/api/v1/ecommerce/reports/${reportId}`
  );
  return response;
};

export const getReportStatus = async (
  reportId: string
): Promise<ReportStatusResponse> => {
  const response = await getRequestWithAuth(
    `/api/v1/ecommerce/reports/${reportId}/status`
  );
  return response;
};

export const listReports = async (
  connectionId?: string,
  limit: number = 10
): Promise<ReportSummary[]> => {
  const params = new URLSearchParams();
  if (connectionId) params.append('connection_id', connectionId);
  params.append('limit', limit.toString());

  const response = await getRequestWithAuth(
    `/api/v1/ecommerce/reports/?${params.toString()}`
  );
  return response;
};

export const getLatestReport = async (
  connectionId: string
): Promise<ReportDetail> => {
  const response = await getRequestWithAuth(
    `/api/v1/ecommerce/reports/connection/${connectionId}/latest`
  );
  return response;
};
